import React, { useEffect, useState } from 'react';
import {
  CAF_CATEGORY_OVERRIDE_VALUES, CafCategoryOverride, CafCorpusAction, CafCorpusEntry, CafExtractionField,
  CafExtractionSchema, CafFinalResponseStance, ClaimFieldOption, listClaimFields,
} from '../../api/cafCorpus';
import { ACTION_COPY, ACTION_ORDER, actionCopy } from './copy';
import { FinalResponseSelect } from './FinalResponseSelect';
import { useFinalResponseSupported } from './finalResponseSupport';
import { Icon } from './Icon';
import { Modal } from './Overlay';

export interface DataRow {
  key: string;
  lookFor: string;
  claimField: string;
  /** The stored field this row came from, so an edit keeps its description, required flag and id. */
  original?: CafExtractionField;
}

export interface LabelDraft {
  action: CafCorpusAction | '';
  finalResponse: CafFinalResponseStance | '';
  why: string;
  classification: CafCategoryOverride | '';
  copyData: boolean;
  rows: DataRow[];
  archiveWithRefs: boolean;
}

export interface LabelWrite {
  action: CafCorpusAction;
  note: string;
  /** Absent on a backend without the setting (DraftRules). */
  final_response_stance?: CafFinalResponseStance;
  categoryOverride?: CafCategoryOverride | null;
  extraction_schema: CafExtractionSchema | null;
  archive_override_refs?: boolean;
}

export const DUPLICATE_FIELD = 'Each claim field can only be used once.';
const MAX_ROWS = 12;

let rowSeq = 0;
const rowKey = () => {
  rowSeq += 1;
  return `row-${rowSeq}`;
};
const blankRow = (): DataRow => ({ key: rowKey(), lookFor: '', claimField: '' });

export const emptyDraft = (): LabelDraft => ({
  action: '', finalResponse: '', why: '', classification: '', copyData: false, rows: [blankRow()], archiveWithRefs: false,
});

export const draftFromEntry = (entry: CafCorpusEntry): LabelDraft => {
  const fields = entry.extraction_schema?.fields ?? [];
  return {
    action: entry.correct_action,
    finalResponse: entry.final_response_stance ?? '',
    why: entry.note ?? '',
    classification: entry.category_override ?? '',
    copyData: Boolean(entry.extraction_schema?.enabled && fields.length),
    rows: fields.length
      ? fields.map((field) => ({ key: rowKey(), lookFor: field.label, claimField: field.targetClaimField, original: field }))
      : [blankRow()],
    archiveWithRefs: entry.archive_override_refs === true,
  };
};

/** Final fix wave C1/C2: `finalResponse: false` on a backend without the
 *  final-response setting (useFinalResponseSupported), where it is neither
 *  asked for nor sent. Left out, the setting is required, as today. */
export interface DraftRules { finalResponse?: boolean }

export const draftProblems = (draft: LabelDraft, rules: DraftRules = {}): string[] => {
  const problems: string[] = [];
  if (!draft.action) problems.push('Choose what should happen.');
  if (rules.finalResponse !== false && !draft.finalResponse) problems.push('Say whether this is a final response.');
  if (!draft.why.trim()) problems.push('Say why.');
  if (draft.copyData) {
    if (draft.rows.some((row) => !row.lookFor.trim() || !row.claimField)) problems.push('Fill in each data row or remove it.');
    const targets = draft.rows.map((row) => row.claimField).filter(Boolean);
    if (new Set(targets).size !== targets.length) problems.push(DUPLICATE_FIELD);
    if (draft.rows.length > MAX_ROWS) problems.push(`Use ${MAX_ROWS} data rows or fewer.`);
  }
  return problems;
};

export const draftIsValid = (draft: LabelDraft, rules: DraftRules = {}) => draftProblems(draft, rules).length === 0;

export const draftToWrite = (
  draft: LabelDraft,
  claimFields: ClaimFieldOption[],
  previous?: CafCorpusEntry | null,
  rules: DraftRules = {},
): LabelWrite => {
  const action = draft.action as CafCorpusAction;
  let schema: CafExtractionSchema | null = null;
  if (draft.copyData) {
    const used = new Set<string>();
    schema = {
      enabled: true,
      fields: draft.rows.map((row, index) => {
        const option = claimFields.find((field) => field.field === row.claimField);
        const base = row.original && row.original.targetClaimField === row.claimField ? row.original : undefined;
        let id = base?.id ?? `field_${index + 1}`;
        while (used.has(id)) id = `${id}_${index + 1}`;
        used.add(id);
        const dataType = option?.dataType ?? base?.dataType ?? 'string';
        const field: CafExtractionField = {
          id, label: row.lookFor.trim(), description: base?.description ?? '', dataType,
          targetClaimField: row.claimField, required: base?.required ?? false,
        };
        if (dataType === 'enum') field.options = option?.options ?? base?.options ?? [];
        return field;
      }),
    };
  }
  const write: LabelWrite = { action, note: draft.why.trim(), extraction_schema: schema };
  if (rules.finalResponse !== false && draft.finalResponse) write.final_response_stance = draft.finalResponse;
  if (draft.classification) write.categoryOverride = draft.classification;
  else if (previous?.category_override) write.categoryOverride = null;
  if (action === 'AUTO_ARCHIVE') write.archive_override_refs = draft.archiveWithRefs;
  else if (previous) write.archive_override_refs = false;
  return write;
};

/** The write's setting as a payload field, or nothing at all when it has none. */
export const stanceField = (write: LabelWrite): { final_response_stance?: CafFinalResponseStance } =>
  (write.final_response_stance ? { final_response_stance: write.final_response_stance } : {});

let claimFieldsRequest: Promise<ClaimFieldOption[]> | null = null;

/** The claim-field allowlist, requested once per page load. */
export const useClaimFieldOptions = (): ClaimFieldOption[] => {
  const [options, setOptions] = useState<ClaimFieldOption[]>([]);
  useEffect(() => {
    let alive = true;
    // Promise.resolve: the repo's obsolete @types/axios types these calls as IPromise, which has no finally.
    const request = claimFieldsRequest ?? Promise.resolve(listClaimFields()).catch((err) => {
      claimFieldsRequest = null;
      throw err;
    });
    claimFieldsRequest = request;
    request.then((next) => { if (alive) setOptions(next); }).catch(() => undefined);
    return () => { alive = false; };
  }, []);
  return options;
};

export const ActionPill: React.FC<{ action: string | null | undefined; past?: boolean }> = ({ action, past }) => {
  const copy = actionCopy(action);
  return (
    <span className={`cur-pill cur-pill--${copy.tone}`}>
      {copy.emailsClient && <Icon name="mail" size="xs" />}
      {past ? copy.did : copy.label}
    </span>
  );
};

export const ExplainOutcomesModal: React.FC<{ onClose: () => void }> = ({ onClose }) => (
  <Modal title="What each outcome does" size="wide" onClose={onClose}
    footer={<button type="button" className="ui-btn ui-btn--primary ui-btn--md" onClick={onClose}>Got it</button>}>
    <p className="cur-hint">
      The AI does this for every new email that looks like the group you label. Colours match the charts on How it&apos;s doing.
    </p>
    <ul className="cur-explain">
      {ACTION_ORDER.map((action) => (
        <li key={action}>
          <div>
            <ActionPill action={action} />
            {ACTION_COPY[action].emailsClient && <span className="cur-emails">Emails the client</span>}
          </div>
          <div>{ACTION_COPY[action].explain}</div>
        </li>
      ))}
    </ul>
  </Modal>
);

export interface LabelFormProps {
  idPrefix: string;
  value: LabelDraft;
  onChange: (next: LabelDraft) => void;
  claimFields: ClaimFieldOption[];
  showMore?: boolean;
  allowArchiveWithRefs?: boolean;
  disabled?: boolean;
  /** Rendered right under "What should happen?" (Teach the AI's Workflow field). */
  afterAction?: React.ReactNode;
  hideWhy?: boolean;
}

export const LabelForm: React.FC<LabelFormProps> = ({
  idPrefix, value, onChange, claimFields, showMore = true, allowArchiveWithRefs = true, disabled = false, afterAction, hideWhy = false,
}) => {
  const [explaining, setExplaining] = useState(false);
  const askFinalResponse = useFinalResponseSupported();
  const set = (patch: Partial<LabelDraft>) => onChange({ ...value, ...patch });
  const setRow = (key: string, patch: Partial<DataRow>) =>
    set({ rows: value.rows.map((row) => (row.key === key ? { ...row, ...patch } : row)) });
  const isArchive = value.action === 'AUTO_ARCHIVE';
  const moreOpen = Boolean(value.classification || value.copyData || value.archiveWithRefs);
  const duplicate = value.copyData && draftProblems(value).includes(DUPLICATE_FIELD);

  return (
    <div className="cur-form">
      <div className="cur-field">
        <div className="cur-label-row">
          <label htmlFor={`${idPrefix}-action`}>What should happen?</label>
          <button type="button" className="cur-info-btn" onClick={() => setExplaining(true)}>
            <Icon name="info" size="sm" />What do these mean?
          </button>
        </div>
        <select id={`${idPrefix}-action`} className="cur-input" value={value.action} disabled={disabled}
          onChange={(event) => {
            const action = event.target.value as CafCorpusAction | '';
            set({ action, archiveWithRefs: action === 'AUTO_ARCHIVE' ? value.archiveWithRefs : false });
          }}>
          <option value="">Choose what should happen…</option>
          {ACTION_ORDER.map((action) => (
            <option key={action} value={action}>
              {ACTION_COPY[action].label}{ACTION_COPY[action].emailsClient ? ' (emails the client)' : ''}
            </option>
          ))}
        </select>
      </div>
      {askFinalResponse && (
        <FinalResponseSelect id={`${idPrefix}-final`} value={value.finalResponse} disabled={disabled}
          onChange={(finalResponse) => set({ finalResponse })}
          hint="The AI follows this for similar emails from this sender. Provisional, holding and acknowledgement letters are not final responses." />
      )}
      {afterAction}
      {!hideWhy && <div className="cur-field">
        <label htmlFor={`${idPrefix}-why`}>Why?</label>
        <textarea id={`${idPrefix}-why`} className="cur-input" rows={3} maxLength={4000} value={value.why}
          disabled={disabled} aria-describedby={`${idPrefix}-why-hint`}
          placeholder="For example: This lender's acknowledgement letters. Nothing for us to do, so attach and mark done."
          onChange={(event) => set({ why: event.target.value })} />
        <span id={`${idPrefix}-why-hint`} className="cur-hint">
          The AI reads this when it sees similar emails. Say what these emails are and why this is right.
        </span>
      </div>}
      {showMore && (
        <details className="cur-more" open={moreOpen}>
          <summary>
            <span className="cur-chev"><Icon name="chev-r" size="sm" /></span>More options
            <span className="cur-sum-hint">classification, data, references</span>
          </summary>
          <div className="cur-more-body">
            <div className="cur-field">
              <label htmlFor={`${idPrefix}-cls`}>Classification <span className="cur-opt">optional</span></label>
              <select id={`${idPrefix}-cls`} className="cur-input" value={value.classification} disabled={disabled}
                onChange={(event) => set({ classification: event.target.value as CafCategoryOverride | '' })}>
                <option value="">None</option>
                {CAF_CATEGORY_OVERRIDE_VALUES.map((value2) => <option key={value2} value={value2}>{value2}</option>)}
                {/* A stored value the list no longer offers stays visible and selected, never dropped. */}
                {value.classification && !CAF_CATEGORY_OVERRIDE_VALUES.includes(value.classification) && (
                  <option value={value.classification}>{value.classification}</option>
                )}
              </select>
            </div>
            <div>
              <label className="cur-check">
                <input type="checkbox" checked={value.copyData} disabled={disabled}
                  onChange={(event) => set({ copyData: event.target.checked })} />
                <span>Copy data from these emails into the claim
                  <span className="cur-hint">For example, pull the agreement number into the claim&apos;s agreement field.</span>
                </span>
              </label>
              {value.copyData && (
                <div className="cur-xrows" style={{ marginTop: 10 }}>
                  {value.rows.map((row, index) => (
                    <div key={row.key} className="cur-xrow">
                      <input className="cur-input" aria-label={`What to look for (${index + 1})`} placeholder="What to look for"
                        value={row.lookFor} disabled={disabled} onChange={(event) => setRow(row.key, { lookFor: event.target.value })} />
                      <span className="cur-arrow" aria-hidden="true">→</span>
                      <select className="cur-input" aria-label={`Claim field (${index + 1})`} value={row.claimField}
                        disabled={disabled} onChange={(event) => setRow(row.key, { claimField: event.target.value })}>
                        <option value="">Choose a claim field…</option>
                        {claimFields.map((field) => <option key={field.field} value={field.field}>{field.label}</option>)}
                        {/* Stored but not (or not yet) in the live list: shown by its value, never blank. */}
                        {row.claimField && !claimFields.some((field) => field.field === row.claimField) && (
                          <option value={row.claimField}>{row.claimField}</option>
                        )}
                      </select>
                      <button type="button" className="cur-icon-btn" aria-label={`Remove data row ${index + 1}`}
                        disabled={disabled || value.rows.length === 1}
                        onClick={() => set({ rows: value.rows.filter((other) => other.key !== row.key) })}>
                        <Icon name="x" size="sm" />
                      </button>
                    </div>
                  ))}
                  {duplicate && <span className="cur-hint" role="alert">{DUPLICATE_FIELD}</span>}
                  <div>
                    <button type="button" className="cur-linkish" disabled={disabled || value.rows.length >= MAX_ROWS}
                      onClick={() => set({ rows: [...value.rows, blankRow()] })}>+ Add another</button>
                  </div>
                </div>
              )}
            </div>
            {allowArchiveWithRefs && (
              <label className={`cur-check${isArchive ? '' : ' is-disabled'}`}>
                <input type="checkbox" checked={value.archiveWithRefs} disabled={disabled || !isArchive}
                  onChange={(event) => set({ archiveWithRefs: event.target.checked })} />
                <span>Also archive emails that quote a reference
                  <span className="cur-hint">
                    {isArchive
                      ? 'Only tick this if the references in these emails never relate to our claims.'
                      : 'Only available when the email is archived.'}
                  </span>
                </span>
              </label>
            )}
          </div>
        </details>
      )}
      {explaining && <ExplainOutcomesModal onClose={() => setExplaining(false)} />}
    </div>
  );
};
